import { addMinutes, format, isValid, parse } from "date-fns";
import { formatAsIso } from "./formatAsIso";
import { formatOrDefault } from "./formatOrDefault";
import { isoDateRangeRegex } from "../regex/regex";

export const enum DateStringFormat {
	UTC = "ISODate",
	Slash = "dd/MM/yyyy HH:mm:ss",
	Dash = "dd-MM-yyyy HH:mm:ss",
}

export const localDateStringToUtc = (
	dateString: string,
	stringFormatInput: DateStringFormat = DateStringFormat.UTC,
	stringFormatOutput: DateStringFormat = DateStringFormat.UTC,
): string => {
	// Parses the formatted local date string
	const localDate = stringFormatInput === DateStringFormat.UTC ? new Date(dateString) : parse(dateString, stringFormatInput, new Date());

	if (!isValid(localDate) || isNaN(localDate.getTime())) {
		throw new Error("Bad dateString format: " + dateString);
	}

	// Returns date to UTC 0 time offset
	return stringFormatOutput === DateStringFormat.UTC ? formatAsIso(localDate) : format(addMinutes(localDate, localDate.getTimezoneOffset()), stringFormatOutput);
};

export const localToUtc = (localDate: Date): Date => {
	if (!isValid(localDate) || isNaN(localDate.getTime())) {
		throw new Error("Bad localDate object: " + localDate);
	}

	// Returns date to UTC 0 time offset
	return addMinutes(localDate, localDate.getTimezoneOffset());
};
export const localToUtcNullable = (date: Date | null): Date | null => {
	if (date === null) return null;
	return localToUtc(date);
};

export const convertUtcDateTimeRangeToLocalDate = (dateTimeRange: string, outputFormat: string = "dd/MM/yyyy"): string => {
	if (!dateTimeRange) return dateTimeRange;
	const [startDateTime] = dateTimeRange.split(/[-#]/);
	const localStartDate = utcToLocalDate(startDateTime);
	return format(localStartDate, outputFormat);
};

export const formatToDateTimeRange = (fromDaysAgo: number, toDaysAgo: number, outputFormat: string, splitter: string = "#"): string => {
	const today = setBeginningOfDay(new Date()) ?? new Date();

	const fromDate = new Date(today);
	fromDate.setDate(today.getDate() - fromDaysAgo);
	const toDate = new Date(today);
	toDate.setDate(today.getDate() - toDaysAgo);

	return `${formatOrDefault(localToUtcNullable(fromDate), outputFormat, "")}${splitter}${formatOrDefault(localToUtcNullable(setEndOfDay(toDate)), outputFormat, "")}`;
};

export const formatToDateTimeRangeFromHoursAgo = (fromHoursAgo: number, outputFormat: string, splitter: string = "#"): string => {
	const now = new Date(new Date().setMilliseconds(0));
	const fromDate = new Date(now);
	fromDate.setHours(now.getHours() - fromHoursAgo);
	const toDate = new Date(now);

	return `${formatOrDefault(localToUtcNullable(fromDate), outputFormat, "")}${splitter}${formatOrDefault(localToUtcNullable(toDate), outputFormat, "")}`;
};

export const formatDateToDateTimeRange = (fromDaysAgo: Date | null, toDaysAgo: Date | null, outputFormat: string, splitter: string = "#"): string =>
	`${formatOrDefault(localToUtcNullable(fromDaysAgo), outputFormat, "")}${splitter}${formatOrDefault(localToUtcNullable(setEndOfDay(toDaysAgo)), outputFormat, "")}`;

export const formatDateTimeToDateTimeRange = (fromDaysAgo: Date | null, toDaysAgo: Date | null, outputFormat: string, splitter: string = "#"): string =>
	`${formatOrDefault(localToUtcNullable(fromDaysAgo), outputFormat, "")}${splitter}${formatOrDefault(localToUtcNullable(toDaysAgo), outputFormat, "")}`;

export const utcToLocalDate = (dateString: string, dateStringFormat: string | null = null): Date => {
	const localDate = dateStringFormat === null ? new Date(dateString) : parse(dateString, dateStringFormat, new Date());

	if (!isValid(localDate) || isNaN(localDate.getTime())) {
		throw new Error("Bad dateString format: " + dateString);
	}

	if (dateString.indexOf("Z") === -1) {
		// Convert dateString to local time (Necessary for dates without timezone from API)
		localDate.setTime(localDate.getTime() - localDate.getTimezoneOffset() * 60 * 1000);
	}
	return localDate;
};

export const tryParseUtcToLocalDate = (dateString: string, dateStringFormat: string | null = null): Date | null => {
	try {
		return utcToLocalDate(dateString, dateStringFormat);
	} catch (e) {
		return null;
	}
};

export const utcToLocalDateNullable = (dateString: string | null, dateStringFormat: string | null = null): Date | null => {
	if (dateString === null) return null;
	return utcToLocalDate(dateString, dateStringFormat);
};

export const setEndOfDay = (date: Date | null): Date | null => {
	if (!date) return null;
	const endOfDayDate = new Date(date);
	endOfDayDate.setHours(23);
	endOfDayDate.setMinutes(59);
	endOfDayDate.setSeconds(59);
	endOfDayDate.setMilliseconds(999);
	return endOfDayDate;
};

export const setBeginningOfDay = (date: Date | null): Date | null => {
	if (!date) return null;
	const beginningOfDay = new Date(date);
	beginningOfDay.setHours(0);
	beginningOfDay.setMinutes(0);
	beginningOfDay.setSeconds(0);
	beginningOfDay.setMilliseconds(0);
	return beginningOfDay;
};

export const initialValueIsIsoDateRangeFormat = (valueToCheck: string): boolean => (!valueToCheck ? false : isoDateRangeRegex.test(valueToCheck));

export const formatDateForSettingsDialog = (inputString: string): string => {
	try {
		const parts: string[] = inputString.split("#");
		const outputParts: string[] = [];
		parts.forEach(part => {
			const tIndex: number = part.indexOf("T");
			if (tIndex !== -1) {
				const datePart: string = part.slice(0, tIndex);
				const [year, month, day] = datePart.split("-");
				const formattedDate: string = `${day}/${month}/${year}`;
				outputParts.push(formattedDate);
			} else {
				outputParts.push(part);
			}
		});
		return outputParts.join("-");
	} catch (e) {
		return inputString;
	}
};
